/**
 * Shared haptics helpers — Sister Signal.
 *
 * Two flavours, used sparingly:
 *
 *   • lightTap()    — a single soft impact. Use on a meaningful one-tap
 *                     completion: marking a goal done, saving a reflection,
 *                     ticking a checklist item, every phase of a breathing
 *                     cycle. Should feel like a pen ticking a box.
 *
 *   • successPulse() — a slightly stronger notification-style pulse.
 *                      Reserved for **completion of a whole thing**:
 *                      finishing an entire lesson, finishing all the cycles
 *                      of a breathing pattern. Should never fire more than
 *                      once per user action.
 *
 * Both are gated by the user's accessibility preference (default ON) so
 * post-concussion / sensory-sensitive users can mute every haptic with a
 * single Settings toggle. Both are no-ops on web / when the
 * @capacitor/haptics plugin is absent, and swallow all errors silently —
 * haptics must never crash a flow.
 */
import { isNative } from "./native";

const HAPTICS_KEY = "ss_haptics_enabled";

/** Read the user preference; default to enabled (true). */
export function isHapticsEnabled() {
  try {
    const v = window.localStorage.getItem(HAPTICS_KEY);
    return v === null ? true : v === "1";
  } catch { return true; }
}

/** Toggle the user preference. Persisted across launches. */
export function setHapticsEnabled(enabled) {
  try { window.localStorage.setItem(HAPTICS_KEY, enabled ? "1" : "0"); }
  catch { /* ignore */ }
}

export function lightTap() {
  if (!isNative()) return;
  if (!isHapticsEnabled()) return;
  import("@capacitor/haptics")
    .then(({ Haptics, ImpactStyle }) => {
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    })
    .catch(() => { /* plugin absent — silent */ });
}

export function successPulse() {
  if (!isNative()) return;
  if (!isHapticsEnabled()) return;
  import("@capacitor/haptics")
    .then(({ Haptics, NotificationType }) => {
      Haptics.notification({ type: NotificationType.Success }).catch(() => {});
    })
    .catch(() => { /* plugin absent — silent */ });
}
